import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Lock } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { PLATFORM, usePlatformTheme } from "../../lib/platformBrand";

const BrandSide = () => (
  <div className="relative hidden overflow-hidden bg-brand p-12 text-white lg:flex lg:flex-col lg:justify-between" data-testid="login-brand-panel">
    <div aria-hidden="true" className="absolute -right-32 -top-32 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(58,176,225,0.35),transparent_65%)]" />
    <div aria-hidden="true" className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full border border-dashed border-gold/40" />
    <p className="relative font-mono text-[11px] uppercase tracking-[0.3em] text-white/60">{PLATFORM.name} · Nairobi, Kenya</p>
    <div className="relative">
      <h2 className="font-serif text-5xl font-bold leading-[1.05]">Booking engines,<br /><span className="text-gold">built for every hotel.</span></h2>
      <p className="mt-6 max-w-md text-base font-light leading-relaxed text-white/75">
        Create, brand and manage direct-booking sites for every property on eRes, with M-Pesa payments and embeddable widgets.
      </p>
    </div>
    <p className="relative font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">{PLATFORM.tagline}</p>
  </div>
);

const ForgotForm = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="forgot-form">
      {sent ? (
        <p className="rounded-xl bg-brand-light/50 p-4 text-sm text-ink/80" data-testid="forgot-sent">If an admin account exists for {email}, we've emailed a link to reset the password. It expires in 60 minutes.</p>
      ) : (
        <>
          <p className="text-sm text-ink/60">Enter your admin email and we'll send you a link to choose a new password.</p>
          <input className="field-input" type="email" placeholder="Email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          {err && <p className="text-sm text-red-600">{err}</p>}
          <button className="btn-primary w-full" disabled={busy} data-testid="forgot-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Email me a reset link</button>
        </>
      )}
      <button type="button" className="block w-full text-center text-xs text-gold underline" onClick={onBack} data-testid="forgot-back">Back to sign in</button>
    </form>
  );
};

export default function AdminLogin() {
  usePlatformTheme();
  const [forgot, setForgot] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/login", form);
      qc.setQueryData(["me"], data);
      navigate(data.platform_role === "superadmin" ? "/superadmin" : "/admin");
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-sand font-sans text-ink lg:grid-cols-[1.1fr_1fr]" data-testid="admin-login-page">
      <BrandSide />
      <div className="grid place-items-center bg-linen px-4 py-12">
        <div className="w-full max-w-sm rounded-3xl border border-ink/10 bg-white p-8 shadow-[0_30px_60px_-20px_rgba(38,53,98,0.25)]">
          <img src={PLATFORM.logo} alt={PLATFORM.name} className="mx-auto h-20 w-auto" data-testid="login-logo" />
          <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-ink/50">{PLATFORM.product}</p>
          <h1 className="mt-1 text-center font-serif text-3xl font-bold text-brand">{forgot ? "Forgot password" : "Sign in"}</h1>
          {forgot ? <ForgotForm onBack={() => setForgot(false)} /> : (<form onSubmit={submit} data-testid="login-form">
          <div className="mt-6 space-y-3">
            <input className="field-input" type="email" placeholder="Email" required autoComplete="username"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-login-email" />
            <input className="field-input" type="password" placeholder="Password" required autoComplete="current-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-login-password" />
          </div>
          {err && <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{err}</p>}
          <button className="btn-primary mt-5 w-full" disabled={busy} data-testid="admin-login-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}Sign in
          </button>
          <button type="button" className="mt-3 block w-full text-center text-xs text-ink/60 underline hover:text-ink" onClick={() => setForgot(true)} data-testid="forgot-password-link">Forgot password?</button>
          </form>)}
          <p className="mt-6 text-center text-xs text-ink/50">Need help? <a className="text-gold underline" href={`mailto:${PLATFORM.email}`}>{PLATFORM.email}</a></p>
        </div>
      </div>
    </div>
  );
}
